<!-- ──────────────────────────────────────────────────────────────────────────
     Barra de navegación horizontal (mega-menú) — niveles grupo → categoría → módulo.
     Solo visible en pantallas anchas (el layout la oculta bajo xl).
─────────────────────────────────────────────────────────────────────────── -->
<div class="tn-surface relative border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">

  <!-- Nivel 1: grupos + botón de fijar -->
  <div class="mx-auto flex max-w-(--breakpoint-2xl) items-stretch px-4 xl:px-6">
  <nav
    class="flex flex-1 items-stretch gap-1 overflow-x-auto no-scrollbar"
    [attr.aria-label]="'common.mainNav' | transloco"
  >
    @for (group of sidebarData()?.groups; track group.id) {
      <!-- Todos los grupos abren su mega-menú con el desglose (categoría → módulo),
           aunque la categoría tenga un único módulo, para ser consistentes con el
           menú lateral (que siempre muestra la jerarquía completa). -->
      <button
        type="button"
        (click)="toggleGroup(group.id)"
        [attr.aria-expanded]="openGroupId() === group.id"
        class="flex cursor-pointer items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-3.5 text-sm font-medium transition-colors"
        [ngClass]="isGroupActive(group) || openGroupId() === group.id
          ? 'border-brand-500 text-brand-600 dark:text-brand-400'
          : 'border-transparent text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'"
      >
        <span class="truncate">{{ groupLabel(group) }}</span>
        @if (group.system) {
          <app-badge variant="light" color="primary">SYS</app-badge>
        }
        <svg
          class="h-4 w-4 transition-transform duration-200"
          [ngClass]="{ 'rotate-180': openGroupId() === group.id }"
          viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4.79175 7.396L10.0001 12.6043L15.2084 7.396"
            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    }
  </nav>

    <!-- Buscador de la barra superior — con desplegable de resultados anclado. -->
    <div class="relative flex shrink-0 items-center self-center pl-2">
      <div class="relative">
        <span
          class="pointer-events-none absolute inset-y-0 left-0 flex w-9 items-center justify-center text-gray-400 dark:text-gray-500"
        >
          <i class="bi bi-search text-[14px]"></i>
        </span>
        <input
          #topNavSearchInput
          type="text"
          [value]="searchQuery()"
          (input)="onSearchInput($event)"
          (keydown)="onSearchKeydown($event)"
          [placeholder]="'sidebar.search.placeholder' | transloco"
          [attr.aria-label]="'sidebar.search.aria' | transloco"
          autocomplete="off"
          spellcheck="false"
          class="h-9 w-48 rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-14 text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:w-64 focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-500/20 dark:border-gray-800 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-brand-500 dark:focus:bg-gray-900"
        />
        @if (isSearching()) {
          <button
            type="button"
            (click)="clearSearchAndFocus()"
            [attr.aria-label]="'sidebar.search.clear' | transloco"
            class="absolute inset-y-0 right-2 my-auto flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <i class="bi bi-x-lg text-[12px]"></i>
          </button>
        } @else {
          <kbd
            class="pointer-events-none absolute inset-y-0 right-2 my-auto flex h-5 select-none items-center gap-1 rounded border border-gray-200 bg-white px-1.5 font-sans text-[10px] font-medium tracking-wide text-gray-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-500"
          >
            {{ shortcutHint }}
          </kbd>
        }
      </div>

      <!-- Desplegable de resultados (lista plana tipo command-palette) -->
      @if (isSearching()) {
        <div
          class="tn-surface absolute right-0 top-full z-[96] mt-2 max-h-[70vh] w-80 overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl dark:border-gray-800 dark:bg-gray-900"
          aria-live="polite"
        >
          @let results = searchResults();
          @if (results.length) {
            <ul class="flex flex-col gap-0.5">
              @for (res of results; track res.module.id; let i = $index) {
                <li [id]="'tn-search-result-' + i">
                  <a
                    [routerLink]="res.module.url"
                    (click)="onResultClick()"
                    class="flex items-start gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors"
                    [ngClass]="i === activeResultIndex() || isActive(res.module.url)
                      ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white'"
                  >
                    <span
                      class="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-500 dark:bg-brand-900/20 dark:text-brand-400"
                      aria-hidden="true"
                    >
                      <i [class]="'bi ' + res.category.icon"></i>
                    </span>
                    <span class="flex min-w-0 flex-col">
                      <span class="truncate font-medium">
                        @for (part of highlightParts(moduleLabel(res.module)); track $index) {
                          <span [class.sidebar-search-hl]="part.match">{{ part.text }}</span>
                        }
                        @if (res.group.system) {
                          <span class="system-identificador-menu">SYS</span>
                        }
                      </span>
                      <span class="truncate text-xs text-gray-400 dark:text-gray-500">
                        {{ ('sidebar.search.in' | transloco) }} {{ categoryLabel(res.category) }}
                      </span>
                    </span>
                  </a>
                </li>
              }
            </ul>
          } @else {
            <div class="flex flex-col items-center gap-1 px-4 py-8 text-center">
              <i class="bi bi-search text-2xl text-gray-300 dark:text-gray-600"></i>
              <p class="mt-1 text-sm font-medium text-gray-500 dark:text-gray-400">
                {{ 'sidebar.search.noResults' | transloco: { query: searchQuery() } }}
              </p>
              <p class="text-xs text-gray-400 dark:text-gray-500">
                {{ 'sidebar.search.noResultsHint' | transloco }}
              </p>
            </div>
          }
        </div>
      }
    </div>

    <!-- Fijar el mega-menú: al fijar, el panel del grupo abierto pasa a estar EN FLUJO
         (empuja el contenido hacia abajo) y queda estático (no se cierra al navegar ni al
         hacer clic fuera). Solo existe en la barra superior. -->
    <button
      type="button"
      (click)="togglePinned()"
      [attr.aria-pressed]="pinned()"
      class="ml-1 flex shrink-0 items-center justify-center px-2 transition-colors"
      [ngClass]="pinned()
        ? 'text-brand-600 dark:text-brand-400'
        : 'text-gray-400 hover:text-brand-600 dark:text-gray-500 dark:hover:text-brand-400'"
      [title]="(pinned() ? 'layout.topnav.unpin' : 'layout.topnav.pin') | transloco"
      [attr.aria-label]="(pinned() ? 'layout.topnav.unpin' : 'layout.topnav.pin') | transloco"
    >
      <!-- Pin recto (bi-pin) inclinado con CSS: ladeado cuando está suelto, recto al fijar. -->
      <i class="bi text-base leading-none transition-transform duration-200"
         [ngClass]="pinned() ? 'bi-pin-fill rotate-0' : 'bi-pin rotate-45'"></i>
    </button>
  </div>

  <!-- Niveles 2 y 3: mega-menú del grupo abierto. Fijado → en flujo (empuja el contenido);
       normal → superpuesto (absolute) sobre el contenido. -->
  @if (openGroup(); as g) {
    <div
      class="tn-surface border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
      [style.backgroundColor]="surfaceColor()"
      [ngClass]="pinned()
        ? 'relative border-t border-gray-200 dark:border-gray-800'
        : 'absolute left-0 right-0 top-full z-[95] shadow-xl'"
    >
      <div class="mx-auto max-w-(--breakpoint-2xl) px-4 py-6 xl:px-6">
        <div class="grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          @for (ctg of g.categories; track ctg.id) {
            <div class="min-w-0">
              <!-- Cabecera de categoría (nivel 2) -->
              <div class="mb-3 flex items-center gap-2 border-b border-gray-100 pb-2 dark:border-gray-800">
                <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-500 dark:bg-brand-900/20 dark:text-brand-400">
                  <i [class]="'bi ' + ctg.icon"></i>
                </span>
                <span class="truncate text-sm font-semibold text-gray-800 dark:text-gray-100" [attr.title]="categoryLabel(ctg)">
                  {{ categoryLabel(ctg) }}
                </span>
              </div>

              <!-- Módulos (nivel 3) -->
              <ul class="space-y-0.5">
                @for (mod of ctg.modules; track mod.id) {
                  <li>
                    <a
                      [routerLink]="mod.url"
                      (click)="closeMenu()"
                      class="flex items-center justify-between gap-2 truncate rounded-lg px-2.5 py-2 text-sm transition-colors"
                      [ngClass]="isActive(mod.url)
                        ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/20 dark:text-brand-300'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white'"
                    >
                      <span class="truncate">{{ moduleLabel(mod) }}</span>
                      @if (g.system) {
                        <span class="system-identificador-menu">SYS</span>
                      }
                    </a>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      </div>
    </div>
  }
</div>
